import React, { useState, useEffect } from "react";
import Block from "../Block/Block";
import { makeStyles } from "@material-ui/core/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@apollo/client";
import { GET_ACCORDION_BLOCK_QUERY } from "./AccordionBlock.query";

const AccordionBlock = (props) => {
    const useStyles = makeStyles((theme) => ({
        root: {
            width: '100%',
        },
        accordionWrapper: {
            width: '100%',
        },
        accordionItem: {
            marginBottom: theme.spacing(2),
            width: '100%',
            display: 'block',
        },
        accordionContent: {
            display: 'block',
            width: '100%',
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            padding: theme.spacing(3),
        },
    }));

    const classes = useStyles();

    // Get the accordion block ID from props
    const accordionBlockId = props.block.accordion_block?.id;

    // Fetch accordion sections using the separate query
    const { loading, error, data } = useQuery(GET_ACCORDION_BLOCK_QUERY, {
        variables: { id: accordionBlockId },
        skip: !accordionBlockId, // Skip the query if we don't have an ID
    });

    // Extract accordion sections from the query result
    const accordionSections = data?.accordionBlock?.accordion_sections || [];

    if (loading) {
        return (
            <div className={classes.root} id={`accordion-root-${accordionBlockId}`} data-testid={`accordion-root-${accordionBlockId}`}>
                <div className={classes.accordionWrapper} id={`accordion-wrapper-${accordionBlockId}`} data-testid={`accordion-wrapper-${accordionBlockId}`}>
                    <div className={classes.loadingContainer} id={`accordion-loading-${accordionBlockId}`} data-testid={`accordion-loading-${accordionBlockId}`}>
                        <CircularProgress data-testid={`accordion-progress-${accordionBlockId}`} />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        console.error("Error loading accordion sections:", error);
        return (
            <div className={classes.root} id={`accordion-root-${accordionBlockId}`} data-testid={`accordion-root-${accordionBlockId}`}>
                <div className={classes.accordionWrapper} id={`accordion-wrapper-${accordionBlockId}`} data-testid={`accordion-wrapper-${accordionBlockId}`}>
                    <Typography color="error" id={`accordion-error-${accordionBlockId}`} data-testid={`accordion-error-${accordionBlockId}`}>Error loading accordion content. Please try again later.</Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root} id={`accordion-root-${accordionBlockId}`} data-testid={`accordion-root-${accordionBlockId}`}>
            <div className={classes.accordionWrapper} id={`accordion-wrapper-${accordionBlockId}`} data-testid={`accordion-wrapper-${accordionBlockId}`}>
                {accordionSections.map((section, index) => {
                    // Use section.id as the stable identifier, fallback to index only if necessary
                    const sectionId = section.id || `index-${index}`;
                    return (
                        <Accordion 
                            key={`accordion-${accordionBlockId}-${sectionId}`} 
                            className={classes.accordionItem}
                            defaultExpanded={props.block.accordion_block?.defaultExpanded}
                            id={`accordion-section-${accordionBlockId}-${sectionId}`}
                            data-testid={`accordion-section-${accordionBlockId}-${sectionId}`}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`accordion-content-${accordionBlockId}-${sectionId}`}
                                id={`accordion-header-${accordionBlockId}-${sectionId}`}
                                data-testid={`accordion-header-${accordionBlockId}-${sectionId}`}
                            >
                                <Typography 
                                    variant="h6" 
                                    id={`accordion-title-${accordionBlockId}-${sectionId}`}
                                    data-testid={`accordion-title-${accordionBlockId}-${sectionId}`}
                                >
                                    {section.title || `Section ${index + 1}`}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails 
                                className={classes.accordionContent} 
                                style={{ width: '100%' }}
                                id={`accordion-details-${accordionBlockId}-${sectionId}`}
                                data-testid={`accordion-details-${accordionBlockId}-${sectionId}`}
                            >
                                {section.blocks && section.blocks.map((block, blockIndex) => {
                                    // Use block.id as the stable identifier, fallback to blockIndex only if necessary
                                    const blockId = block.id || `index-${blockIndex}`;
                                    return (
                                        <Block 
                                            key={`accordion-block-${blockId}`}
                                            form={props.form} 
                                            handleChange={props.handleChange} 
                                            block={block}
                                            id={`accordion-block-${accordionBlockId}-${sectionId}-${blockId}`}
                                            data-testid={`accordion-block-${accordionBlockId}-${sectionId}-${blockId}`}
                                        />
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </div>
        </div>
    );
};

export default AccordionBlock;
